/* eslint-disable no-param-reassign */
import { REHYDRATE } from 'redux-persist/constants';

import attachments from '../attachments/attachments';
import documentsCollection from '../commons/rest/documentsRest';
import restService from '../commons/rest/restService';
import { CategoryEntity, DocumentEntity } from '../../../shared/src/entity';
import { displayMessage, INFO, SUCCESS } from '../commons/components/message/messageDucks';
import { closeModal, openModal } from '../commons/components/modal/modalDucks';

/**
 * ====================================
 * The actions
 * ====================================
 */
const GET_DOCUMENTS_START = 'GET_DOCUMENTS_START';
export const GET_DOCUMENTS_SUCCESS = 'GET_DOCUMENTS_SUCCESS';
const GET_DOCUMENTS_FAILURE = 'GET_DOCUMENTS_FAILURE';
const CHANGE_ACTIVE_CATEGORY = 'CHANGE_ACTIVE_CATEGORY';

const GET_ATTACHMENT_START = 'GET_ATTACHMENT_START';
const GET_ATTACHMENT_PERSIST = 'GET_ATTACHMENT_PERSIST';
const GET_ATTACHMENT_SUCCESS = 'GET_ATTACHMENT_SUCCESS';
const GET_ATTACHMENT_FAILURE = 'GET_ATTACHMENT_FAILURE';
const GET_ATTACHMENTS_SUCCESS = 'GET_ATTACHMENTS_SUCCESS';

/**
 * ====================================
 * Helpers
 * ====================================
 */
const displaySynchronizationStartMessage = (dispatch, nbAttachments) =>
  dispatch(displayMessage(
    'ic-lap ic-sm',
    `Début de la synchronisation de ${nbAttachments} document${nbAttachments === 1 ? '' : 's'}`,
    INFO,
    0
  ));

const displaySynchronizationEndMessage = (dispatch, nbAttachments, nbErrors) => {
  let msg = `Synchronisation terminée de ${nbAttachments} document${nbAttachments === 1 ? '' : 's'}`;
  if (nbErrors === 1) {
    msg += ` (${nbErrors} document non synchronisé)`;
  } else if (nbErrors > 1) {
    msg += ` (${nbErrors} documents non synchronisés)`;
  }

  dispatch(displayMessage(
    'ic-lap ic-sm',
    msg,
    SUCCESS
  ));
};

const displaySynchronizationProgressMessage = (dispatch, nbAttachments, nbSynchronized) => {
  const s = nbSynchronized === 1 ? '' : 's';
  return dispatch(displayMessage(
    'ic-lap ic-sm',
    `${nbSynchronized} document${s} sur ${nbAttachments} synchronisé${s}`,
    INFO,
    0
  ));
};

const compareStrings = (s1, s2, options) => (s1 || '').localeCompare(s2 || '', options);

/**
 * Sort documents by (in order)
 * - month (desc)
 * - high priority
 * - start date (sec)
 * - name (asc)
 */
export const compareDocumentsForDisplay = (d1, d2) => {
  if (d1.displayMonth && !d2.displayMonth) {
    return -1;
  }
  if (d2.displayMonth && !d1.displayMonth) {
    return 1;
  }

  const monthComparison = compareStrings(d2.displayMonth, d1.displayMonth);
  if (monthComparison) {
    return monthComparison;
  }

  const subCategoryComparison = compareStrings(d1.subCategory, d2.subCategory);
  if (subCategoryComparison) {
    return subCategoryComparison;
  }

  const highPriorityComparison = d2.highPriority - d1.highPriority;
  if (highPriorityComparison) {
    return highPriorityComparison;
  }

  const startDateComparison = compareStrings(d2.startDate, d1.startDate);
  if (startDateComparison) {
    return startDateComparison;
  }

  return compareStrings(d1.title, d2.title, { sensitivity: 'base' });
};

/**
 * Sort documents by type (everything, then VIDEO), then first category, then display order
 */
const compareDocumentsForDownload = (d1, d2) => {
  const typeComparison = (d1.type === 'VIDEO') - (d2.type === 'VIDEO');
  if (typeComparison) {
    return typeComparison;
  }

  const categoryRankComparison = d1.firstCategoryRank - d2.firstCategoryRank;
  if (categoryRankComparison) {
    return categoryRankComparison;
  }

  return compareDocumentsForDisplay(d1, d2);
};

const flatten = arr => Array.prototype.concat.apply([], arr);

const getAttachmentIds = documents => flatten(documents.map((doc) => {
  const attachmentIds = [doc.id];
  if (doc.attachments) {
    doc.attachments.forEach(att => attachmentIds.push(att.id));
  }
  return attachmentIds;
}));

/**
 * ====================================
 * The actions creators
 * ====================================
 */

/**
 * Functions that recursively downloads and stores attachments.
 * When an attachment is successfully stored in IndexedDB, its matching
 * wrapper is removed from the "wrappers" array and the next attachment
 * is downloaded.
 */
const downloadAttachments = (wrappers, nbAttachments, documentsInErrors) => (dispatch) => {
  const documentWrapper = wrappers.shift();
  const doc = documentWrapper.document;
  const attachmentId = documentWrapper.attachmentId;
  dispatch({ type: GET_ATTACHMENT_START, payload: { documentId: doc.id, attachmentId } });

  return restService.get(doc.getAttachmentUrl(attachmentId))
    .then((response) => {
      if (response.ok) {
        return response.arrayBuffer()
          .then(arrayBuffer => ({
            arrayBuffer,
            contentType: response.headers.get('Content-Type'),
          }));
      }
      return Promise.reject();
    })
    .then(({ arrayBuffer, contentType }) => {
      dispatch({ type: GET_ATTACHMENT_PERSIST, payload: { documentId: doc.id, attachmentId } });
      return attachments.saveAttachment(attachmentId || doc.id, arrayBuffer, contentType);
    })
    .then(() => {
      dispatch({ type: GET_ATTACHMENT_SUCCESS, payload: { documentId: doc.id, attachmentId } });
    })
    .catch((error) => {
      dispatch({ type: GET_ATTACHMENT_FAILURE, payload: { documentId: doc.id, attachmentId } });
      /*
       * Managing Safari iOS out of memory
       */
      if (error && error.message &&
        (error.message.indexOf('An unknown error occurred within Indexed Database') >= 0
          || error.message.indexOf('An internal error was encountered in the Indexed Database server') >= 0)) {
        dispatch(openModal(
          'Une erreur fatale est survenue. Veuillez relancer le navigateur.',
          'Fermer',
          () => dispatch(closeModal())
        ));
        return Promise.reject(error);
      }
      if (documentWrapper.attempts === 0) {
        documentWrapper.attempts = 1;
        wrappers.push(documentWrapper);
      } else {
        documentsInErrors.push(documentWrapper.document);
      }
      return Promise.resolve();
    })
    .then(() => {
      if (wrappers.length !== 0) {
        const nbSynchronized = nbAttachments - wrappers.length;
        displaySynchronizationProgressMessage(dispatch, nbAttachments, nbSynchronized);
        return dispatch(downloadAttachments(wrappers, nbAttachments, documentsInErrors));
      }
      displaySynchronizationEndMessage(dispatch, nbAttachments, documentsInErrors.length);
      dispatch({ type: GET_ATTACHMENTS_SUCCESS });
      return Promise.resolve();
    });
};

/**
 * Loads the attachments of the given documents and stores the attachments
 * into the IndexedDB database
 *
 * @param documents The documents collection whose attachments must be loaded.
 */
const loadDocumentAttachments = (documents = []) => (dispatch) => {
  const documentsWrappers = flatten([...documents]
    .sort(compareDocumentsForDownload)
    .map((doc, index) => {
      const wrappers = [];
      if (doc.loading || doc.updating) {
        wrappers.push({
          document: doc,
          index: index + 1,
          attempts: 0,
        });
      }
      if (doc.attachments) {
        doc.attachments.forEach((att) => {
          if (att.loading || att.updating) {
            wrappers.push({
              document: doc,
              attachmentId: att.id,
              index: index + 1,
              attempts: 0,
            });
          }
        });
      }
      return wrappers;
    }));

  if (!documentsWrappers || documentsWrappers.length === 0) {
    dispatch({ type: GET_ATTACHMENTS_SUCCESS });
    return Promise.resolve();
  }

  displaySynchronizationStartMessage(dispatch, documentsWrappers.length);

  return dispatch(downloadAttachments(documentsWrappers, documentsWrappers.length, []));
};

export const loadDocuments = () => (dispatch, getState) => {
  dispatch({ type: GET_DOCUMENTS_START });

  const oldAttachmentIds = getAttachmentIds(getState().documents.data);

  return documentsCollection.getAll()
    .then((response) => {
      dispatch({ type: GET_DOCUMENTS_SUCCESS, payload: response.body(false) });

      return dispatch(loadDocumentAttachments(getState().documents.data));
    })
    .then(() => {
      const newAttachmentIds = getAttachmentIds(getState().documents.data);
      const deletedAttachmentIds = oldAttachmentIds.filter(id => !newAttachmentIds.includes(id));
      if (deletedAttachmentIds.length) {
        attachments.deleteAttachments(deletedAttachmentIds);
      }
    })
    .catch(err => dispatch({ type: GET_DOCUMENTS_FAILURE, payload: err }));
};

export const changeActiveCategory = activeCategory => ({
  type: CHANGE_ACTIVE_CATEGORY,
  payload: activeCategory,
});

/**
 * ====================================
 * The reducer
 * ====================================
 */
const initialState = {
  data: [],
  categories: [],
  activeCategory: null,
  error: false,
  loading: false,
  lastUpdated: null,
};

const setLoadingOrUpdating = (oldAttachment, newAttachment) => {
  if (newAttachment.type === 'LINK') {
    return;
  }
  if (!oldAttachment || oldAttachment.loading) {
    newAttachment.loading = true;
  } else if (oldAttachment.updating) {
    newAttachment.updating = true;
    newAttachment.oldFileName = oldAttachment.oldFileName;
  } else if (newAttachment.audit.updateDateTime > oldAttachment.audit.updateDateTime) {
    newAttachment.updating = true;
    newAttachment.oldFileName = oldAttachment.fileName;
  }
};

const documentsToState = (documents, oldDocuments) => {
  const categories = [];
  const data = documents.map((document) => {
    if (document.categories) {
      for (const category of document.categories) {
        if (!categories.find(cat => cat.id === category.id)) {
          categories.push(new CategoryEntity(category));
        }
      }
    }

    const documentEntity = new DocumentEntity(document);

    if (oldDocuments) {
      const oldDocument = oldDocuments.find(doc => doc.id === document.id);
      setLoadingOrUpdating(oldDocument, documentEntity);

      documentEntity.attachments = (documentEntity.attachments || []).map((attachment) => {
        const oldAttachment = ((oldDocument && oldDocument.attachments) || [])
          .find(att => att.id === attachment.id);
        setLoadingOrUpdating(oldAttachment, attachment);
        return attachment;
      });
    }

    return documentEntity;
  });
  data.sort(compareDocumentsForDisplay);
  categories.sort((cat1, cat2) => cat1.rank - cat2.rank);
  return ({
    data,
    categories,
    activeCategory: categories[0],
  });
};

export default (state = initialState, action) => {
  switch (action.type) {
    case GET_DOCUMENTS_START:
      return {
        ...state,
        error: false,
        loading: true,
      };
    case GET_DOCUMENTS_FAILURE:
      return {
        ...state,
        error: true,
        loading: false,
      };
    case GET_DOCUMENTS_SUCCESS:
      return {
        ...state,
        ...documentsToState(action.payload, state.data || []),
        error: false,
      };
    case GET_ATTACHMENTS_SUCCESS:
      return {
        ...state,
        error: false,
        loading: false,
        lastUpdated: new Date(),
      };
    case GET_ATTACHMENT_SUCCESS: {
      const { documentId, attachmentId } = action.payload;
      return {
        ...state,
        data: state.data.map((doc) => {
          if (doc.id !== documentId) {
            return doc;
          }
          const document = { ...doc };
          if (attachmentId) {
            document.attachments = document.attachments.map((att) => {
              if (att.id !== attachmentId) {
                return att;
              }
              const newAtt = { ...att };
              delete newAtt.loading;
              delete newAtt.updating;
              delete newAtt.oldFileName;
              return newAtt;
            });
          } else {
            delete document.loading;
            delete document.updating;
            delete document.oldFileName;
          }
          return new DocumentEntity(document);
        }),
      };
    }
    case CHANGE_ACTIVE_CATEGORY:
      return {
        ...state,
        activeCategory: action.payload,
      };
    case REHYDRATE:
      if (action.payload.documents && action.payload.documents.data) {
        return {
          ...state,
          ...documentsToState(action.payload.documents.data),
          lastUpdated: new Date(action.payload.documents.lastUpdated),
        };
      }
      return state;
    default:
      return state;
  }
};



// WEBPACK FOOTER //
// ./book/src/documents/documentsDucks.js